import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TeacherActivity, TeacherActivityTargetLink, TeacherTarget } from '@isophi/pap-api';
import { ManagerActivity, ManagerActivityTargetLink } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { finalize, forkJoin, Observable, takeUntil, tap } from 'rxjs';
import { BaseComponent } from '../base-component/base.component';
import { ToastService } from '../../services/toast.service';
import { activityTypeData } from '../../constants/data';
import { BaseModeEnum } from '../portfolio-plan-base-item/portfolio-plan-base-item.component';

@Component({
    selector: 'isophi-app-portfolio-plan-linked-activites',
    templateUrl: './plan-linked-activites.component.html',
    styleUrls: ['./plan-linked-activites.component.scss'],
    standalone: false
})
export class PlanLinkedActivitesComponent extends BaseComponent implements OnInit {
  @Input() teacherTargetId!: string;
  @Input() allowEditMode = true;
  @Input() useAbsoluteHref = false;

  @Input() fetchTeacherActivityTargetLinks!: (teacherTargetId: string) => Observable<TeacherActivityTargetLink[] | ManagerActivityTargetLink[]>;
  @Input() fetchTeacherActivities!: () => Observable<TeacherActivity[] | ManagerActivity[]>;
  @Input() deleteTeacherActivityTargetLink!: (linkId: string) => Observable<void>;
  @Input() createTeacherActivityTargetLink!: (teacherActivityId: string, teacherTargetId: string) => Observable<TeacherActivityTargetLink>;
  @Input() buildTeacherActivityRouteLink!: (teacherActivityId: string) => string[] | string;

  @Output() teacherActivityTargetLinksUpdated = new EventEmitter<TeacherTarget>();
  @Output() teacherActivityTargetLinksCountUpdated = new EventEmitter<number>();

  loadingTeacherActivityTargetLinks = false;
  loadingTeacherActivities = false;

  teacherActivityTargetLinks: TeacherActivityTargetLink[]= [];
  teacherActivities: TeacherActivity[] = [];

  existingActivityUuids: string[] = [];
  selectedActivityUuids: string[] = [];

  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly cd = inject(ChangeDetectorRef);

  activityTypeData = activityTypeData;

  ngOnInit(): void {
    this.loadTeacherActivityTargetLinks();
  }

  handleModeChanged(mode: BaseModeEnum): void {
    switch (mode) {
      case BaseModeEnum.VIEW:
        this.loadTeacherActivityTargetLinks();
        break;
      case BaseModeEnum.EDIT:
        this.loadTeacherActivities();
        break;
    }
  }

  loadTeacherActivityTargetLinks(): void {
    this.loadingTeacherActivityTargetLinks = true;
    this.fetchTeacherActivityTargetLinks(this.teacherTargetId).pipe(
      takeUntil(this.ngUnsubscribe$),
      tap(() => this.loadingTeacherActivityTargetLinks = false),
      tap(() => this.cd.markForCheck()),
    ).subscribe({
      next: (teacherActivityTargetLinks) => {
        this.teacherActivityTargetLinks = teacherActivityTargetLinks as TeacherActivityTargetLink[];
        this.teacherActivityTargetLinksCountUpdated.emit(teacherActivityTargetLinks.length);
        this.existingActivityUuids = teacherActivityTargetLinks.map((link) => link.activity.id);
        this.selectedActivityUuids = [...this.existingActivityUuids];
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherTargetListErrorToast'));
      },
    });
  }

  loadTeacherActivities(): void {
    this.loadingTeacherActivities = true;
    this.fetchTeacherActivities().pipe(
      takeUntil(this.ngUnsubscribe$),
      tap(() => this.loadingTeacherActivities = false),
      tap(() => this.cd.markForCheck()),
    ).subscribe({
      next: (teacherActivities) => {
        this.teacherActivities = teacherActivities as TeacherActivity[];
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherTargetListErrorToast'));
      },
    });
  }

  handleTeacherActivitySelect(event: Event, activityId: string): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      this.selectedActivityUuids.push(activityId);
    } else {
      this.selectedActivityUuids = this.selectedActivityUuids.filter((id) => id !== activityId);
    }
  }

  handleLinkDelete(linkId: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.deleteTeacherActivityTargetLink(linkId).pipe(
      takeUntil(this.ngUnsubscribe$),
    ).subscribe({
      next: () => {
        this.loadTeacherActivityTargetLinks();
        this.toastService.success(this.translocoService.translate('shared/teacherActivityTargetLinkDeleteSuccessToast'));
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('shared/teacherActivityTargetLinkDeleteErrorToast'));
      },
    });
  }

  save(): void {
    const createLinkObservables = this.selectedActivityUuids.filter((activityId) => !this.existingActivityUuids.includes(activityId)).map((activityId) => {
      return this.createTeacherActivityTargetLink(this.teacherTargetId, activityId);
    });

    if (createLinkObservables.length <= 0) {
      return;
    }

    forkJoin(createLinkObservables)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        finalize(() => this.loadTeacherActivityTargetLinks())
      )
      .subscribe({
        next: () => {
          if (this.teacherActivityTargetLinks.length > 0) {
            this.teacherActivityTargetLinksUpdated.emit(this.teacherActivityTargetLinks[0].target);
          }
          this.toastService.success(this.translocoService.translate('shared/teacherActivityTargetLinkCreateSuccessToast'));
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('shared/teacherActivityTargetLinkCreateErrorToast'));
        },
      });
  }
}
